import {
  Appbar,
  Button,
  Card,
  Chip,
  Text,
  useTheme,
  Dialog,
  Portal,
  Checkbox,
  IconButton,
} from 'react-native-paper';
import { useQuery, useMutation } from '@apollo/client';
import React, {
  useContext, useState, useEffect,
} from 'react';
import {
  View, FlatList, RefreshControl, Alert, ScrollView,
} from 'react-native';
import { styles } from './ManageAccessPolicies.style';
import {
  CREATE_ACCESS_POLICY,
  DELETE_ACCESS_POLICY,
  UPDATE_ACCESS_POLICY,
  ASSIGN_POLICY_TO_USER,
  UNASSIGN_POLICY_FROM_USER,
} from '../../gql/access-policy/mutation';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import { ModalContext } from '../../context/ModalContext';
import ConfirmationModal from '../../modals/Confirmation/ConfirmationModal';
import AccessPolicyDialogs from './AccessPolicyDialogs';
import { AGE_CATEGORY, GRADE } from '../../utils/constant';
import { GET_ACCESS_POLICIES } from '../../gql/access-policy/query';
import { GET_COACHES } from '../../gql/user/query';

const parseRulesToChips = (rules) => {
  const chips = [];
  try {
    const parsedRules = typeof rules === 'string' ? JSON.parse(rules) : rules || {};
    const profileRules = parsedRules.profile || {};

    if (profileRules.grade && profileRules.grade.in) {
      profileRules.grade.in.forEach((gradeKey) => {
        chips.push({ type: 'grade', label: GRADE[gradeKey] || gradeKey });
      });
    }

    if (profileRules.OR) {
      profileRules.OR.forEach((condition) => {
        if (condition.dateOfBirth && condition.dateOfBirth.gte && condition.dateOfBirth.lte) {
          const gteDate = new Date(condition.dateOfBirth.gte);
          const lteDate = new Date(condition.dateOfBirth.lte);
          const maxYear = (new Date().getFullYear() - gteDate.getFullYear()) - 1;
          const minYear = new Date().getFullYear() - lteDate.getFullYear();

          const category = Object.values(AGE_CATEGORY).find(
            (c) => c.minYear === minYear && c.maxYear === maxYear,
          );
          if (category) {
            chips.push({ type: 'category', label: category.name });
          }
        }
      });
    }

    if (profileRules.dateOfBirth) {
      if (profileRules.dateOfBirth.gte) {
        const maxDate = new Date(profileRules.dateOfBirth.gte);
        const age = new Date().getFullYear() - maxDate.getFullYear();
        chips.push({ type: 'age', label: `< ${age} ans` });
      }
      if (profileRules.dateOfBirth.lte) {
        const minDate = new Date(profileRules.dateOfBirth.lte);
        const age = new Date().getFullYear() - minDate.getFullYear();
        chips.push({ type: 'age', label: `> ${age} ans` });
      }
    }
  } catch (e) {
    // ignore
  }
  return chips;
};

function PolicyRules({ rules }) {
  const chips = parseRulesToChips(rules);

  if (chips.length === 0) {
    return <Text>Aucune règle définie</Text>;
  }

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 }}>
      {chips.map((chip, index) => (
        <Chip key={index} style={{ margin: 2 }} icon="tag-outline">
          {chip.label}
        </Chip>
      ))}
    </View>
  );
}

function PolicyCoaches({ users }) {
  if (!users || users.length === 0) {
    return <Text>Aucun coach attaché</Text>;
  }

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 }}>
      {users.map((user) => (
        <Chip key={user.id} style={{ margin: 2 }} icon="account-outline">
          {`${user.firstName} ${user.lastName}`}
        </Chip>
      ))}
    </View>
  );
}

function ManageCoachesDialog({
  visible, onDismiss, policy, coaches, onSave,
}) {
  const [selectedCoaches, setSelectedCoaches] = useState([]);

  useEffect(() => {
    if (policy && policy.users) {
      setSelectedCoaches(policy.users.map((u) => u.id));
    } else {
      setSelectedCoaches([]);
    }
  }, [policy]);

  const handleToggleCoach = (coachId) => {
    setSelectedCoaches((prev) => (prev.includes(coachId)
      ? prev.filter((id) => id !== coachId)
      : [...prev, coachId]));
  };

  if (!policy) return null;

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss}>
        <Dialog.Title>
          Gérer les coachs pour
          {' '}
          {policy.name}
        </Dialog.Title>
        <Dialog.Content>
          <ScrollView style={{ maxHeight: 400 }}>
            {(coaches || []).map((coach) => (
              <Checkbox.Item
                key={coach.id}
                label={`${coach.firstName} ${coach.lastName}`}
                status={selectedCoaches.includes(coach.id) ? 'checked' : 'unchecked'}
                onPress={() => handleToggleCoach(coach.id)}
              />
            ))}
          </ScrollView>
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss}>Annuler</Button>
          <Button onPress={() => onSave(selectedCoaches)}>Sauvegarder</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
}

function ManageAccessPoliciesScreen() {
  const theme = useTheme();
  const {
    data, loading, error, refetch,
  } = useQuery(GET_ACCESS_POLICIES);
  const { data: coachesData, loading: coachesLoading } = useQuery(GET_COACHES, {
    fetchPolicy: 'network-only',
  });
  const {
    toggleConfirmationModal,
    displayConfirmationModal,
  } = useContext(ModalContext);

  const [createAccessPolicy] = useMutation(CREATE_ACCESS_POLICY);
  const [updateAccessPolicy] = useMutation(UPDATE_ACCESS_POLICY);
  const [deleteAccessPolicy] = useMutation(DELETE_ACCESS_POLICY);
  const [assignPolicyToUser] = useMutation(ASSIGN_POLICY_TO_USER);
  const [unassignPolicyFromUser] = useMutation(UNASSIGN_POLICY_FROM_USER);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [coachesDialogVisible, setCoachesDialogVisible] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedPolicyForCoaches, setSelectedPolicyForCoaches] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const openModal = (policy) => {
    if (policy) {
      setSelectedPolicy(policy);
      setName(policy.name);
      setDescription(policy.description);
      setRules(JSON.stringify(policy.rules, null, 2));
    } else {
      setSelectedPolicy(null);
      setName('');
      setDescription('');
      setRules('');
    }
    setDialogVisible(true);
  };

  const hideModal = () => {
    setDialogVisible(false);
  };

  const openCoachesDialog = (policy) => {
    setSelectedPolicyForCoaches(policy);
    setCoachesDialogVisible(true);
  };

  const hideCoachesDialog = () => {
    setCoachesDialogVisible(false);
  };

  const handleSaveCoaches = async (selectedCoachIds) => {
    if (!selectedPolicyForCoaches) return;

    const policyId = selectedPolicyForCoaches.id;
    const initialCoachIds = selectedPolicyForCoaches.users.map((u) => u.id);

    const coachesToAssign = selectedCoachIds.filter((id) => !initialCoachIds.includes(id));
    const coachesToUnassign = initialCoachIds.filter((id) => !selectedCoachIds.includes(id));

    try {
      await Promise.all([
        ...coachesToAssign.map((userId) => assignPolicyToUser({ variables: { userId, policyId } })),
        ...coachesToUnassign.map((userId) => unassignPolicyFromUser({
          variables: { userId, policyId },
        })),
      ]);
      refetch();
      hideCoachesDialog();
    } catch (e) {
      Alert.alert('Erreur', e.message);
    }
  };

  const handleSave = async () => {
    try {
      const parsedRules = JSON.parse(rules);
      if (selectedPolicy) {
        await updateAccessPolicy({
          variables: {
            id: selectedPolicy.id,
            name,
            description,
            rules: parsedRules,
          },
        });
      } else {
        await createAccessPolicy({
          variables: { name, description, rules: parsedRules },
        });
      }
      refetch();
      hideModal();
    } catch (e) {
      Alert.alert('Erreur', 'Les règles JSON sont invalides ou une autre erreur est survenue.');
    }
  };

  const handleDelete = (policy) => {
    toggleConfirmationModal({
      title: 'Supprimer la politique',
      message: 'Etes-vous sûr de vouloir supprimer cette politique ?',
      handleSubmit: async () => {
        try {
          await deleteAccessPolicy({ variables: { id: policy.id } });
          refetch();
        } catch (e) {
          Alert.alert('Erreur', e.message);
        }
      },
    });
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  if (loading || coachesLoading) {
    return <LoadingIndicator text="Chargement des politiques..." />;
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text>
          Erreur:
          {error.message}
        </Text>
      </View>
    );
  }

  return (
    <>
      {displayConfirmationModal.display && <ConfirmationModal />}
      <Appbar.Header statusBarHeight={0}>
        <Appbar.Content title="Gérer les politiques d'accès" />
        <Appbar.Action
          icon="plus-box"
          onPress={() => openModal(null)}
        />
      </Appbar.Header>
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        <FlatList
          data={data?.accessPolicies}
          keyExtractor={(item) => item.id.toString()}
          renderItem={({ item }) => (
            <Card style={styles.card}>
              <Card.Title
                title={item.name}
                subtitle={item.description}
              />
              <Card.Content>
                <Text variant="titleMedium" style={{ marginBottom: 5 }}>Règles</Text>
                <PolicyRules rules={item.rules} />
                <View style={{
                  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                }}
                >
                  <Text variant="titleMedium" style={{ marginTop: 15, marginBottom: 5 }}>Coaches</Text>
                  <IconButton icon="cog" onPress={() => openCoachesDialog(item)} />
                </View>
                <PolicyCoaches users={item.users} />
              </Card.Content>
              <Card.Actions>
                <Button onPress={() => openModal(item)}>Modifier</Button>
                <Button
                  disabled={item.users.length > 0}
                  onPress={() => handleDelete(item)}
                >
                  Supprimer
                </Button>
              </Card.Actions>
            </Card>
          )}
          refreshControl={(
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={onRefresh}
            />
          )}
        />
        <AccessPolicyDialogs
          dialogVisible={dialogVisible}
          hideDialog={hideModal}
          currentPolicy={selectedPolicy}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          rules={rules}
          setRules={setRules}
          handleSave={handleSave}
        />
        <ManageCoachesDialog
          visible={coachesDialogVisible}
          onDismiss={hideCoachesDialog}
          policy={selectedPolicyForCoaches}
          coaches={coachesData?.users}
          onSave={handleSaveCoaches}
        />
      </View>
    </>
  );
}

export default ManageAccessPoliciesScreen;
